import { useMutation, useQueryClient } from '@tanstack/react-query';
import { surveyApi, USE_MOCK } from '@/api/client';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useUserContext } from '@/contexts/UserContext';
import type { CreateSurveyRequest, CreateSurveyResponse } from '@/api/types';
import type { SurveyForm } from '../types';

/** Maps the frontend QuestionType to the backend accepted values (see CreateSurveyCommandValidator) */
function toBackendQuestionType(type: string): string {
  const map: Record<string, string> = {
    rating:    'behavior',
    behaviors: 'behavior',
    nps:       'nps',
    open:      'open',
    multiple:  'behavior',
  };
  return map[type] ?? 'behavior';
}

/** Derives SurveyType from the question mix */
function deriveSurveyType(form: SurveyForm): CreateSurveyRequest['type'] {
  const types = new Set(form.questions.map(q => q.type));
  const hasNps = types.has('nps');
  const hasBehaviors = types.has('rating') || types.has('behaviors') || types.has('multiple');
  const hasOpen = types.has('open');

  if (hasOpen && !hasNps && !hasBehaviors) return 'Open';
  if (hasNps && !hasBehaviors && !hasOpen) return 'NPS';
  if (hasBehaviors && hasNps) return 'BehaviorsAndNPS';
  return 'Behaviors';
}

/** Derives ouIds from wizard audienceOUs + audienceSubOUs state (RN-009/RN-007). */
function deriveOuIds(audienceOUs: string[], audienceSubOUs: Record<string, string[]>): string[] {
  if (audienceOUs.length === 0) return ['global'];
  const ids = new Set<string>();
  for (const ouKey of audienceOUs) {
    ids.add(ouKey);
    const sub = audienceSubOUs[ouKey];
    if (sub && sub.length > 0) sub.forEach(s => ids.add(s));
  }
  return [...ids];
}

/** Maps wizard form state to RN-009 audience contract fields. */
function toAudienceContract(form: SurveyForm): Pick<CreateSurveyRequest, 'audienceType' | 'ouIds' | 'targetUserIds' | 'targetMeetingId'> {
  if (form.surveyType === 'personalizada') {
    switch (form.destMode) {
      case 'meeting':
        return { audienceType: 'meeting', ouIds: [], targetMeetingId: form.sessionId || undefined };
      case 'people':
        return { audienceType: 'people', ouIds: [], targetUserIds: form.destPeople };
      case 'ou':
        return { audienceType: 'ou', ouIds: form.destOU ? [form.destOU] : [] };
    }
  }
  return {
    audienceType: 'ou',
    ouIds: deriveOuIds(form.audienceOUs, form.audienceSubOUs),
  };
}

/** Builds a { lang: text } map for a meta translation key (survey_name, survey_desc).
 *  Returns undefined if no enabled languages have a non-empty value for the key. */
function buildMetaTranslations(form: SurveyForm, key: string): Record<string, string> | undefined {
  const result: Record<string, string> = {};
  for (const [lang, map] of Object.entries(form.translations)) {
    if (form.translationsEnabled[lang] === false) continue;
    const v = map[key];
    if (v?.trim()) result[lang] = v;
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

/** Builds the `questions` payload shared by create and update — the single place where
 *  `text` is assembled from sourceLang + translations, instead of duplicating the logic
 *  in index.tsx. */
export function buildQuestionsPayload(form: SurveyForm): CreateSurveyRequest['questions'] {
  return form.questions.map((q, idx) => ({
    order: idx + 1,
    questionType: toBackendQuestionType(q.type),
    dailyHabitId: q.coreBehavior || undefined,
    fundamentalId: q.fundamental || undefined,
    focusType: q.enfoque || undefined,
    presentationMode: q.enfoque === 'individual' ? (q.presentMode || undefined) : undefined,
    sourceLang: q.sourceLang,
    text: {
      [q.sourceLang]: q.text,
      // TranslationsSection stores keys as "q_0", "q_1", etc. (index-based, NOT q.id).
      ...Object.fromEntries(
        Object.entries(form.translations)
          .filter(([lang]) => form.translationsEnabled[lang] !== false)
          .flatMap(([lang, map]) => {
            const v = map[`q_${idx}`];
            return v?.trim() ? [[lang, v]] : [];
          })
      ),
    },
  }));
}

/** Transforms the frontend SurveyForm into the backend CreateSurveyRequest.
 *  RN-009: campaign and participant fields are excluded from the payload for non-general
 *  surveys (Minimum Knowledge / defense-in-depth — backend also sanitizes on its end). */
export function toCreateSurveyRequest(form: SurveyForm): CreateSurveyRequest {
  const isGeneralSurvey = form.surveyType === 'general';

  return {
    name: form.name,
    description: form.description || undefined,
    type: deriveSurveyType(form),
    category: form.surveyType || undefined,
    ...toAudienceContract(form),
    nameTranslations: buildMetaTranslations(form, 'survey_name'),
    descriptionTranslations: buildMetaTranslations(form, 'survey_desc'),
    startDate: form.startDate ? `${form.startDate}T00:00:00Z` : undefined,
    expiresAt: form.expirationDate ? `${form.expirationDate}T00:00:00Z` : undefined,
    questions: buildQuestionsPayload(form),
    // Campaign and participant config are only meaningful for 'general' surveys.
    // Excluded entirely for 'personalizada' / 'pulso_especifica' — backend applies defaults.
    ...(isGeneralSurvey && {
      campaignWeight: form.campaignWeight,
      activeDays: form.activeDays,
      boostUrgente: form.boostUrgente,
      boostDays: form.boostDays,
      minRespondents: form.minRespondents,
      maxRespondents: form.maxRespondents,
    }),
  };
}

export function useCreateSurvey() {
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();          // real MSAL token (production)
  const { demoUser } = useUserContext();         // localStorage demo role (dev)
  // In production, currentUser is set and wins. In dev (auth bypassed), falls back to demoUser.
  // El acceso se decide con la lista completa de roles, nunca con el rol de etiqueta.
  const effectiveRoles = currentUser?.roles ?? [demoUser.role];
  const isGlobalAdmin = effectiveRoles.includes('AdminGlobal');

  return useMutation<CreateSurveyResponse, Error, SurveyForm>({
    mutationFn: (form: SurveyForm) => {
      if (USE_MOCK) {
        // RN-013 Fase 1: todas las encuestas publicadas nacen como Active/Approved.
        return Promise.resolve({
          id: crypto.randomUUID(),
          name: form.name,
          status: 'Active',
          version: 1,
          createdBy: currentUser?.id ?? 'dev-user',
          createdAt: new Date().toISOString(),
        });
      }
      // P4-7 — la identidad viaja en el query param para que el backend registre `createdBy`.
      // `currentUser.id` es el oid real de MSAL; `demoUser.userId` es el GUID del simulador local.
      return surveyApi.createSurvey(
        { ...toCreateSurveyRequest(form), isGlobalAdmin },
        currentUser?.id ?? demoUser.userId,
      );
    },
    onSuccess: () => {
      // Invalida la lista de encuestas para que el AdminView se refresque
      queryClient.invalidateQueries({ queryKey: ['surveys'] });
    },
  });
}
